import { getUsers } from '../../Services/serviceURL';
import { CustomerLite } from '../Plants/CreatePlant';
import type { ProfileData, ProfileSortConfig } from './profileTypes';

export function safeProfileValue(value: unknown): string {
    return typeof value === 'string' && value.trim() !== '' ? value : '';
}

export function mapApiUserToProfile(usr: Record<string, unknown>, useFallbackLabels = false): ProfileData {
    const fallback = useFallbackLabels ? 'N/A' : '';
    return {
        id: (usr.id as string) || Math.random().toString(36).substr(2, 9),
        customer: safeProfileValue(usr.customer) || fallback,
        name: safeProfileValue(usr.name as string) || fallback,
        type: safeProfileValue(usr.type as string) || fallback,
        role: safeProfileValue(usr.role as string) || fallback,
        email: safeProfileValue(usr.email as string) || fallback,
        status: safeProfileValue(usr.status as string) || fallback,
    };
}

function readUserCustomerId(user: Record<string, unknown>): string | undefined {
    return (user.customerId || user.CustomerId || user.customerID) as string | undefined;
}

export function userMatchesCustomerFilter(
    user: Record<string, unknown>,
    customerId: string,
    customers: CustomerLite[]
): boolean {
    if (!customerId.trim()) {
        return true;
    }
    const userCustomerId = readUserCustomerId(user);
    if (userCustomerId === customerId) {
        return true;
    }
    const customerName = safeProfileValue(user.customer as string) || safeProfileValue(user.customerName as string);
    const customerMatch = customers.find(c => c.customerId === customerId);
    return Boolean(customerMatch && customerName.includes(customerMatch.customerName));
}

export function userMatchesTypeFilter(user: Record<string, unknown>, userType: string): boolean {
    if (userType === 'All') {
        return true;
    }
    const type = safeProfileValue(user.type as string).toLowerCase();
    return type === userType.toLowerCase();
}

export function filterApiUsers(
    users: Record<string, unknown>[],
    customerId: string,
    userType: string,
    customers: CustomerLite[]
): Record<string, unknown>[] {
    return users.filter(user =>
        userMatchesCustomerFilter(user, customerId, customers) &&
        userMatchesTypeFilter(user, userType)
    );
}

export function extractCustomersFromUserApi(users: Record<string, unknown>[]): CustomerLite[] {
    const customerMap = new Map<string, CustomerLite>();

    users.forEach((user, index) => {
        const customerName =
            safeProfileValue(user.customer as string) ||
            safeProfileValue(user.customerName as string) ||
            safeProfileValue(user.Customer as string) ||
            safeProfileValue(user.CustomerName as string);

        if (!customerName) {
            return;
        }

        const customerId =
            safeProfileValue(user.customerId as string) ||
            safeProfileValue(user.CustomerId as string) ||
            safeProfileValue(user.id as string) ||
            `temp-${index}`;

        const soldTo =
            safeProfileValue(user.soldTo as string) ||
            safeProfileValue(user.SoldTo as string) ||
            safeProfileValue(user.salesDistrict as string) ||
            safeProfileValue(user.SalesDistrict as string);

        const customerKey = customerName.toLowerCase();
        const existing = customerMap.get(customerKey);
        const hasBetterId = Boolean(customerId && !customerId.startsWith('temp-'));
        const hasSoldTo = Boolean(soldTo);

        if (!existing || hasBetterId || hasSoldTo) {
            customerMap.set(customerKey, {
                id: customerId,
                customerId,
                customerName,
                soldTo,
            });
        }
    });

    return Array.from(customerMap.values()).sort((a, b) =>
        a.customerName.localeCompare(b.customerName)
    );
}

export async function fetchProfileRows(
    customerId: string,
    userType: string,
    customers: CustomerLite[]
): Promise<ProfileData[]> {
    const response = await getUsers('All');
    if (!response?.Data || !Array.isArray(response.Data)) {
        return [];
    }
    const filtered = filterApiUsers(response.Data, customerId, userType, customers);
    return filtered.map(user => mapApiUserToProfile(user, true));
}

export function profileMatchesSearch(profile: ProfileData, searchText: string): boolean {
    if (!searchText.trim()) {
        return true;
    }
    const searchLower = searchText.toLowerCase();
    const fields = [
        profile.customer,
        profile.name,
        profile.type,
        profile.role,
        profile.email,
        profile.status,
    ];
    return fields.some(field => (field || '').toLowerCase().includes(searchLower));
}

export function sortAndFilterProfiles(
    allProfiles: ProfileData[],
    searchText: string,
    sortConfig: ProfileSortConfig
): ProfileData[] {
    const searchFiltered = searchText.trim() === ''
        ? allProfiles
        : allProfiles.filter(profile => profileMatchesSearch(profile, searchText));

    if (!sortConfig) {
        return searchFiltered;
    }

    const sortableItems = [...searchFiltered];
    sortableItems.sort((a, b) => {
        const valA = (a[sortConfig.key] ?? '').toString().toLowerCase();
        const valB = (b[sortConfig.key] ?? '').toString().toLowerCase();
        if (valA < valB) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
    return sortableItems;
}

export function getPaginationDisplay(
    totalItems: number,
    currentPage: number,
    itemsPerPage: number
): { from: number; to: number } {
    if (totalItems === 0) {
        return { from: 0, to: 0 };
    }
    const from = (currentPage - 1) * itemsPerPage + 1;
    const to = Math.min(currentPage * itemsPerPage, totalItems);
    return { from, to };
}

export function isProfileStatusActive(status: string): boolean {
    const normalized = status.toLowerCase();
    return normalized === 'active' || normalized === 'enabled';
}
